import { Box, Button, Grid, Typography } from "@mui/material";
import { push } from "connected-react-router";
import { useDeleteWithConfirmController, useEditContext } from "ra-core";
import { FC, useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Link as RouterLink } from "react-router-dom";
import usePermitGuard from "@/authProvider/usePermitGuard";
import { Content } from "@/components/content/Content";
import DocumentTypeEditor from "@/components/securefiles/DocumentTypeEditor";
import Confirm from "@/components/widgets/Confirm";
import { MANAGE_FILE_CLASSIFICATION_AND_PROTECTION_RULES } from "@/constants";
import { useLmStatesPropagate } from "@/contexts/LocalMessageHookProtocols/useLmStatesPropagate";
import { LocalMessageLocation, useLocalMessagingRoot } from "@/contexts/LocalMessagingRoot";
import { useDefaultSavedHandlers } from "@/controller/defaultSavedHandlers";
import Edit from "@/controller/Edit";
import WithSave from "@/controller/WithSave";
import { RecPatch } from "@/dataProvider/tools";
import { DocumentTemplateResponseDto, DocumentTemplateInput } from "@/gen/api/securefile";
import { OdResourcePageProps } from "@/types/resource";
import { useResourceRegister } from "@/utils/resourceRegister";



const EditDocumentType: FC<OdResourcePageProps&RecPatch> = (props) => {
  const { id, resource } = props
  useResourceRegister({name: 'secure-files-doc-tmpl-upload'})
  const handlers = useDefaultSavedHandlers("Edit Document Type")
  usePermitGuard([[MANAGE_FILE_CLASSIFICATION_AND_PROTECTION_RULES]])
  return (
    <Edit resource={resource} basePath="" id={id} mutationMode="pessimistic" {...handlers}>
      <EditDocumentTypeBody {...props} />
    </Edit>
  )
}

const EditDocumentTypeBody: FC<OdResourcePageProps&RecPatch> = ({
  id, options, resource,
}) => {
  const { record, refetch } = useEditContext<DocumentTemplateResponseDto&RecPatch>()
  const dispatch = useDispatch()
  const [states, setStates] = useState({
    dirty: false,
  })
  useEffect(() => { setStates(v => ({...v, dirty: false })) }, [record])

  const { LocalMessagingRootHost, subscribe, publish } = useLocalMessagingRoot()
  const { assembleSubStates } = useLmStatesPropagate({ 
    key: 'root',
    asUpperStage: {
      dirtyStateInformed: {
        handler() {
          return (_, dirty) => setStates(v => {
            return v.dirty !== dirty ? {...v, dirty} : v
          })
        },
        deps: []
      }
    }
  }, {subscribe, publish })

  const handleSave = (save: (record:any,callbacks?:any)=>void) => async () => {
    try {
      const result = await assembleSubStates(['editor'])
      const payload = result.editor!
      payload.id = id
  
      save(payload, {
        onSuccess: () => {
          refetch && refetch()
        }
      })
    } catch (error) {
      return
    }
  }
  
  const delHandlers = useDefaultSavedHandlers('Delete Document Type')
  delHandlers.onSuccess.merge(useCallback(() => { dispatch(push(`/${resource}`)) }, [dispatch, resource]))
  const {
    open: deleteDialogOpen,
    loading,
    handleDialogOpen,
    handleDialogClose,
    handleDelete,
  } = useDeleteWithConfirmController({
    resource,
    record: { id },
    mutationMode: 'pessimistic',
    ...delHandlers,
  })
  
  return (
    <Content 
      pageName="Edit Document Type" 
      resourceName="Edit" 
      breadcrumbs={[...(options?.breadcrumbs ?? []),
        { label: 'Document Types', path: `/${resource}` }
      ]}
      mainActions={
        <>
          <Button sx={{ mr: theme => `${theme.spacing(4)} !important`}}
            color="error"
            variant="outlined"
            onClick={handleDialogOpen}
          >
            Delete
          </Button>
          <WithSave 
            render={({save, saving}) =>
              <Button
                color="primary"
                disabled={saving || !states.dirty}
                variant="contained"
                onClick={handleSave(save)}
              >
                Save
              </Button>}
          />
          <Button
            color="primary"
            variant="outlined"
            component={RouterLink}
            to={`/${resource}`}
          >
            Cancel
          </Button>
        </>
      }
    >
      <Grid container spacing={4} sx={{ mt: 2 }} >
        <Grid item xs={12}>
          <LocalMessagingRootHost>
            <LocalMessageLocation id="editor">
              <DocumentTypeEditor title="Edit Document Type" />
            </LocalMessageLocation>
          </LocalMessagingRootHost>
        </Grid>
      </Grid>
      <Confirm
        isOpen={deleteDialogOpen}
        title={"Delete Document Type"}
        loading={loading}
        onConfirm={handleDelete}
        onClose={handleDialogClose}
        confirm="Confirm"
        cancel="Cancel"
        content={(
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography
                component="span"
                variant="body2"
                sx={{ ml: 1 }}
              >
                Please confirm whether to proceed with the deletion of this
                {(record?.name) ? ` "${record?.name}" ` : " "}
                Document Type.
              </Typography>
            </Box>
          </Box>
        )}
      />
    </Content>
  )
}

export default EditDocumentType;

